import {
	areHexAdjacent,
	coordKey,
	generateFixedLengthPath,
	getNeighbors,
	hexDistance,
	isValidPath,
	type PathGeneratorConfig,
	SOLUTION_DEMO_PATH_RANGE,
} from "../hex-path";
import { createSeededRng, type SeededRng } from "../hex-path/seeded-rng";
import type {
	HexCoord,
	MapLayout,
	MapNode,
	ModuleMapGraph,
	PathCell,
} from "./map-graph-types";

export type PathDirection =
	| "bottom-top"
	| "top-bottom"
	| "left-right"
	| "right-left";

/** Default tiles between path and grid edge (per axis). */
export const HEXAGON_MAP_DEFAULT_PATH_PADDING = 1;
const HEXAGON_MAP_BRANCH_LOCAL_FALLBACK_RADIUS = 6;
const HEXAGON_MAP_BRANCH_SUBATTEMPTS = 8;

export interface MapPathPadding {
	rows?: number;
	cols?: number;
}

export interface ComputeMapLayoutOptions {
	seed: string;
	exactSeed?: boolean;
	direction?: PathDirection;
	pathPadding?: number | MapPathPadding;
	/** Solution-demo y/x range overrides. */
	yMinRange?: number;
	yMaxRange?: number;
	/**
	 * Primary-axis span as a percentage of the spine length (50–100).
	 * - 100: start→end spans almost the full length (nearly straight)
	 * - 50: start→end spans about half the length (more folded/curved)
	 *
	 * This is interpreted relative to the active `direction` (after transform).
	 */
	majorRangePercent?: number;
	xMaxStepsBack?: number;
	xMaxStepsForward?: number;
	/**
	 * Hard boundary for spine generation away from center on the minor axis.
	 * This maps to the path generator's `minCol/maxCol` band.
	 */
	minorRange?: number;
	maxConsecutiveSameDirectionSteps?: number;
	maxAttempts?: number;
	maxDurationMs?: number;
	logAttempts?: boolean;
	forceStraightSpine?: boolean;
}

export function buildMapLayoutAttemptSeed(
	seed: string,
	attempt: number,
): string {
	return `${seed}:attempt:${attempt}`;
}

function logHexagonMapObservation(
	event: string,
	payload: Record<string, unknown>,
): void {
	console.info(`[hexagon-map] ${event}`, payload);
}

export function resolveMapPathPadding(pathPadding?: number | MapPathPadding): {
	rows: number;
	cols: number;
} {
	if (typeof pathPadding === "number") {
		return { rows: pathPadding, cols: pathPadding };
	}
	return {
		rows: pathPadding?.rows ?? HEXAGON_MAP_DEFAULT_PATH_PADDING,
		cols: pathPadding?.cols ?? HEXAGON_MAP_DEFAULT_PATH_PADDING,
	};
}

interface DisplayGridGeneration {
	config: PathGeneratorConfig;
}

function isVerticalPathDirection(direction: PathDirection): boolean {
	return direction === "bottom-top" || direction === "top-bottom";
}

function resolveMinimumPrimaryAxisSeparation(targetLength: number): number {
	return Math.max(1, Math.ceil(targetLength / 2));
}

function resolveCanonicalPrimaryAxisSeparation(
	targetLength: number,
	direction: PathDirection,
): number {
	const minimumPrimaryAxisSeparation =
		resolveMinimumPrimaryAxisSeparation(targetLength);
	const horizontalExtraSpan = isVerticalPathDirection(direction)
		? 0
		: Math.ceil(minimumPrimaryAxisSeparation / 2);
	return Math.min(
		Math.max(targetLength - 1, 1),
		minimumPrimaryAxisSeparation + horizontalExtraSpan,
	);
}

function resolvePrimaryAxisDistance(
	startCell: HexCoord,
	endCell: HexCoord,
	direction: PathDirection,
): number {
	return isVerticalPathDirection(direction)
		? Math.abs(startCell.row - endCell.row)
		: Math.abs(startCell.col - endCell.col);
}

function offsetHexToCube(
	row: number,
	col: number,
): { x: number; y: number; z: number } {
	const x = -col - Math.floor((row + (row & 1)) / 2);
	const z = row;
	const y = -x - z;
	return { x, y, z };
}

function cubeToOffsetHex(
	x: number,
	_y: number,
	z: number,
): { row: number; col: number } {
	const row = z;
	const col = -x - Math.floor((z + (z & 1)) / 2);
	return { row, col };
}

function rotateCube60Clockwise(coord: { x: number; y: number; z: number }): {
	x: number;
	y: number;
	z: number;
} {
	return {
		x: -coord.z,
		y: -coord.x,
		z: -coord.y,
	};
}

function rotateCube300Clockwise(coord: { x: number; y: number; z: number }): {
	x: number;
	y: number;
	z: number;
} {
	return {
		x: -coord.y,
		y: -coord.z,
		z: -coord.x,
	};
}

function nodeBranchId(node: MapNode): string | undefined {
	return "branchId" in node ? node.branchId : undefined;
}

function nodeBranchRejoins(node: MapNode): boolean {
	return "branchRejoins" in node ? Boolean(node.branchRejoins) : false;
}

function isBranchNode(node: MapNode): boolean {
	return Boolean(nodeBranchId(node));
}

function countSpineNodes(graph: ModuleMapGraph): number {
	return graph.nodes.filter((node) => !isBranchNode(node)).length;
}

/**
 * Generate the spine directly in final grid coordinates. The anchor span is
 * intentionally shorter than the full path length so the seeded walk has room
 * to curve, pause, and backtrack like the solution demo.
 */
function buildDisplayGridGeneration(
	targetLength: number,
	branchNodeCount: number,
	options: ComputeMapLayoutOptions,
): DisplayGridGeneration {
	const padding = resolveMapPathPadding(options.pathPadding);
	const direction = options.direction ?? "bottom-top";
	const canonicalPrimaryAxisSeparation =
		resolveCanonicalPrimaryAxisSeparation(targetLength, direction);
	const majorRangePercent = options.majorRangePercent ?? 100;
	const majorRangePercentClamped = Math.max(
		50,
		Math.min(100, majorRangePercent),
	);
	const desiredSpan = Math.round(
		((targetLength - 1) * majorRangePercentClamped) / 100,
	);
	const primaryAxisSpan = Math.max(
		canonicalPrimaryAxisSeparation,
		1,
		Math.min(targetLength - 1, desiredSpan),
	);
	const secondaryAxisSpread = Math.max(
		6,
		Math.ceil(targetLength / 2) + Math.ceil(branchNodeCount / 2),
	);
	const resolvedMinorRange =
		options.minorRange != null
			? Math.max(1, Math.floor(options.minorRange))
			: null;
	const minorAxisSpread =
		resolvedMinorRange != null ? resolvedMinorRange : secondaryAxisSpread;

	const startRow = primaryAxisSpan + padding.rows;
	const startCol = 0;
	const endRow = startRow - primaryAxisSpan;
	const endCol = 0;
	const rowOffsetToEnd = endRow - startRow;
	const yMinRange =
		options.yMinRange ??
		Math.min(SOLUTION_DEMO_PATH_RANGE.yMinRange, rowOffsetToEnd - 2);
	const yMaxRange = options.yMaxRange ?? 0;
	const topRow = startRow + yMinRange;
	const bottomRow = startRow + yMaxRange;
	const endRowMin = Math.max(topRow, endRow);
	const endRowMax = Math.max(
		endRowMin,
		Math.min(startRow - canonicalPrimaryAxisSeparation, bottomRow),
	);

	return {
		config: {
			startRow,
			startCol,
			endRow,
			endCol,
			endRowMin,
			endRowMax,
			yMinRange,
			yMaxRange,
			xMaxStepsBack:
				options.xMaxStepsBack ?? SOLUTION_DEMO_PATH_RANGE.xMaxStepsBack,
			xMaxStepsForward:
				options.xMaxStepsForward ??
				SOLUTION_DEMO_PATH_RANGE.xMaxStepsForward,
			maxConsecutiveSameDirectionSteps:
				options.maxConsecutiveSameDirectionSteps,
			minCol: endCol - minorAxisSpread,
			maxCol: endCol + minorAxisSpread,
		},
	};
}

function transformCanonicalPathToDirection(
	rawPath: HexCoord[],
	direction: PathDirection,
): HexCoord[] {
	const rotateCoord = (
		coord: HexCoord,
		rotate: (cube: { x: number; y: number; z: number }) => {
			x: number;
			y: number;
			z: number;
		},
	) => {
		const rotated = rotate(offsetHexToCube(coord.row, coord.col));
		return cubeToOffsetHex(rotated.x, rotated.y, rotated.z);
	};

	switch (direction) {
		case "top-bottom":
			return [...rawPath].reverse();
		case "left-right":
			return rawPath.map((coord) =>
				rotateCoord(coord, rotateCube300Clockwise),
			);
		case "right-left":
			return rawPath.map((coord) =>
				rotateCoord(coord, rotateCube60Clockwise),
			);
		default:
			return rawPath;
	}
}

function normalizePathToGrid(
	rawPath: HexCoord[],
	padding: { rows: number; cols: number },
	direction: PathDirection,
): {
	path: HexCoord[];
	rows: number;
	cols: number;
	startCell: HexCoord;
	endCell: HexCoord;
} {
	if (rawPath.length === 0) {
		return {
			path: [],
			rows: padding.rows * 2 + 1,
			cols: padding.cols * 2 + 1,
			startCell: { row: 0, col: 0 },
			endCell: { row: 0, col: 0 },
		};
	}

	const transformedPath = transformCanonicalPathToDirection(
		rawPath,
		direction,
	);
	const transformedMinRow = Math.min(...transformedPath.map((p) => p.row));
	const transformedMaxRow = Math.max(...transformedPath.map((p) => p.row));
	const transformedMinCol = Math.min(...transformedPath.map((p) => p.col));
	const transformedMaxCol = Math.max(...transformedPath.map((p) => p.col));
	const desiredRowShift = padding.rows - transformedMinRow;
	const rowShift =
		Math.abs(desiredRowShift % 2) === 1
			? desiredRowShift + 1
			: desiredRowShift;
	const colShift = padding.cols - transformedMinCol;

	const normalized: HexCoord[] = transformedPath.map((p) => ({
		row: p.row + rowShift,
		col: p.col + colShift,
	}));
	const shiftedMaxRow = transformedMaxRow + rowShift;
	const shiftedMaxCol = transformedMaxCol + colShift;
	const rows = shiftedMaxRow + padding.rows + 1;
	const cols = shiftedMaxCol + padding.cols + 1;

	const startCell = normalized[0] ?? { row: padding.rows, col: padding.cols };
	const endCell = normalized[normalized.length - 1] ?? startCell;

	return { path: normalized, rows, cols, startCell, endCell };
}

interface SpineNodePlacement {
	graphIndex: number;
	spineIndex: number;
	node: MapNode;
	cell: HexCoord;
}

interface BranchPlacement {
	branchId: string;
	cells: HexCoord[];
	replacedSpineIndices: Set<number>;
	anchorCell: HexCoord;
	joinCell: HexCoord | null;
}

type PathWalkDirection = "forward" | "backward" | "both";

class BranchPlacementError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "BranchPlacementError";
	}
}

function parseCoordKeyToCoord(key: string): HexCoord {
	const [row, col] = key.split(",").map(Number);
	return { row: row ?? 0, col: col ?? 0 };
}

function countAdjacentPathNeighbors(
	cell: HexCoord,
	pathCoordSet: Set<string>,
): number {
	let adjacentCount = 0;
	for (const neighbor of getNeighbors(cell.row, cell.col)) {
		if (pathCoordSet.has(coordKey(neighbor.row, neighbor.col))) {
			adjacentCount += 1;
		}
	}
	return adjacentCount;
}

function buildJunctionAllowanceByCoord(
	branchPlacements: Array<Pick<BranchPlacement, "anchorCell" | "joinCell">>,
): Map<string, number> {
	const allowanceByCoord = new Map<string, number>();
	const bump = (cell: HexCoord | null) => {
		if (!cell) {
			return;
		}
		const key = coordKey(cell.row, cell.col);
		allowanceByCoord.set(key, (allowanceByCoord.get(key) ?? 0) + 1);
	};
	for (const placement of branchPlacements) {
		bump(placement.anchorCell);
		bump(placement.joinCell);
	}
	return allowanceByCoord;
}

function hasValidPathDegrees(
	pathCoordSet: Set<string>,
	allowanceByCoord: Map<string, number> = new Map(),
): boolean {
	for (const key of pathCoordSet) {
		const maxDegree = 2 + (allowanceByCoord.get(key) ?? 0);
		if (
			countAdjacentPathNeighbors(
				parseCoordKeyToCoord(key),
				pathCoordSet,
			) > maxDegree
		) {
			return false;
		}
	}
	return true;
}

function isConnectedPathGraph(pathCoordSet: Set<string>): boolean {
	if (pathCoordSet.size <= 1) {
		return true;
	}
	const [startKey] = pathCoordSet;
	if (!startKey) {
		return true;
	}
	const queue = [startKey];
	let queueIndex = 0;
	const visited = new Set<string>([startKey]);
	while (queueIndex < queue.length) {
		const currentKey = queue[queueIndex];
		queueIndex += 1;
		if (!currentKey) {
			continue;
		}
		const current = parseCoordKeyToCoord(currentKey);
		for (const neighbor of getNeighbors(current.row, current.col)) {
			const neighborKey = coordKey(neighbor.row, neighbor.col);
			if (!pathCoordSet.has(neighborKey) || visited.has(neighborKey)) {
				continue;
			}
			visited.add(neighborKey);
			queue.push(neighborKey);
		}
	}
	return visited.size === pathCoordSet.size;
}

function buildDegreeByCoord(pathCoordSet: Set<string>): Map<string, number> {
	const degreeByCoord = new Map<string, number>();
	for (const key of pathCoordSet) {
		degreeByCoord.set(
			key,
			countAdjacentPathNeighbors(parseCoordKeyToCoord(key), pathCoordSet),
		);
	}
	return degreeByCoord;
}

function canAddPathCellIncrementally(params: {
	cell: HexCoord;
	activePathCoordSet: Set<string>;
	degreeByCoord: Map<string, number>;
	allowanceByCoord: Map<string, number>;
}): boolean {
	const { cell, activePathCoordSet, degreeByCoord, allowanceByCoord } =
		params;
	let adjacentCount = 0;
	for (const neighbor of getNeighbors(cell.row, cell.col)) {
		const neighborKey = coordKey(neighbor.row, neighbor.col);
		if (!activePathCoordSet.has(neighborKey)) {
			continue;
		}
		adjacentCount += 1;
		const nextNeighborDegree = (degreeByCoord.get(neighborKey) ?? 0) + 1;
		const maxNeighborDegree = 2 + (allowanceByCoord.get(neighborKey) ?? 0);
		if (nextNeighborDegree > maxNeighborDegree) {
			return false;
		}
	}
	const candidateKey = coordKey(cell.row, cell.col);
	const maxCandidateDegree = 2 + (allowanceByCoord.get(candidateKey) ?? 0);
	return adjacentCount > 0 && adjacentCount <= maxCandidateDegree;
}

function addPathCellToState(params: {
	cell: HexCoord;
	key: string;
	activePathCoordSet: Set<string>;
	degreeByCoord: Map<string, number>;
}): string[] {
	const { cell, key, activePathCoordSet, degreeByCoord } = params;
	const adjacentKeys: string[] = [];
	for (const neighbor of getNeighbors(cell.row, cell.col)) {
		const neighborKey = coordKey(neighbor.row, neighbor.col);
		if (!activePathCoordSet.has(neighborKey)) {
			continue;
		}
		adjacentKeys.push(neighborKey);
		degreeByCoord.set(
			neighborKey,
			(degreeByCoord.get(neighborKey) ?? 0) + 1,
		);
	}
	activePathCoordSet.add(key);
	degreeByCoord.set(key, adjacentKeys.length);
	return adjacentKeys;
}

function removePathCellFromState(params: {
	key: string;
	activePathCoordSet: Set<string>;
	degreeByCoord: Map<string, number>;
	adjacentKeys: string[];
}): void {
	const { key, activePathCoordSet, degreeByCoord, adjacentKeys } = params;
	activePathCoordSet.delete(key);
	degreeByCoord.delete(key);
	for (const adjacentKey of adjacentKeys) {
		const prevDegree = degreeByCoord.get(adjacentKey);
		if (prevDegree === undefined) {
			continue;
		}
		degreeByCoord.set(adjacentKey, prevDegree - 1);
	}
}

function hasValidPathEndpoints(
	pathCoordSet: Set<string>,
	branchPlacements: Array<
		Pick<BranchPlacement, "anchorCell" | "joinCell">
	> = [],
): boolean {
	if (pathCoordSet.size <= 1) {
		return true;
	}
	const allowanceByCoord = buildJunctionAllowanceByCoord(branchPlacements);
	const expectedEndpointCount =
		2 +
		branchPlacements.filter((placement) => placement.joinCell == null)
			.length;
	let endpointCount = 0;
	for (const key of pathCoordSet) {
		const degree = countAdjacentPathNeighbors(
			parseCoordKeyToCoord(key),
			pathCoordSet,
		);
		const maxDegree = 2 + (allowanceByCoord.get(key) ?? 0);
		if (degree === 1) {
			endpointCount += 1;
			continue;
		}
		if (degree < 1 || degree > maxDegree) {
			return false;
		}
	}
	return endpointCount === expectedEndpointCount;
}

function isValidVisiblePathGraph(
	pathCoordSet: Set<string>,
	branchPlacements: Array<
		Pick<BranchPlacement, "anchorCell" | "joinCell">
	> = [],
): boolean {
	const allowanceByCoord = buildJunctionAllowanceByCoord(branchPlacements);
	return (
		hasValidPathDegrees(pathCoordSet, allowanceByCoord) &&
		isConnectedPathGraph(pathCoordSet) &&
		hasValidPathEndpoints(pathCoordSet, branchPlacements)
	);
}

function walkSpineIndices(params: {
	startIndex: number;
	minIndex: number;
	maxIndex: number;
	direction: PathWalkDirection;
	excluded?: Set<number>;
}): number[] {
	const {
		startIndex,
		minIndex,
		maxIndex,
		direction,
		excluded = new Set(),
	} = params;
	const indices: number[] = [];
	const maybePush = (index: number) => {
		if (index < minIndex || index > maxIndex || excluded.has(index)) {
			return;
		}
		indices.push(index);
		excluded.add(index);
	};
	if (direction === "forward") {
		for (let index = startIndex; index <= maxIndex; index += 1) {
			maybePush(index);
		}
		return indices;
	}
	if (direction === "backward") {
		for (let index = startIndex; index >= minIndex; index -= 1) {
			maybePush(index);
		}
		return indices;
	}
	const maxDistance = Math.max(startIndex - minIndex, maxIndex - startIndex);
	for (let distance = 0; distance <= maxDistance; distance += 1) {
		maybePush(startIndex - distance);
		if (distance > 0) {
			maybePush(startIndex + distance);
		}
	}
	return indices;
}

function buildVisiblePathCoordSet(
	spine: HexCoord[],
	branchPlacements: BranchPlacement[],
	extraRemovedSpineIndices: Set<number> = new Set(),
): Set<string> {
	const removedSpineIndices = new Set<number>(extraRemovedSpineIndices);
	for (const placement of branchPlacements) {
		for (const index of placement.replacedSpineIndices) {
			removedSpineIndices.add(index);
		}
	}
	const pathCoordSet = new Set<string>();
	for (let index = 0; index < spine.length; index += 1) {
		const cell = spine[index];
		if (!cell || removedSpineIndices.has(index)) {
			continue;
		}
		pathCoordSet.add(coordKey(cell.row, cell.col));
	}
	for (const placement of branchPlacements) {
		for (const cell of placement.cells) {
			pathCoordSet.add(coordKey(cell.row, cell.col));
		}
	}
	return pathCoordSet;
}

export function buildBranchCorridorIdByCoordKey(
	spine: HexCoord[],
	branchPlacements: BranchPlacement[],
): Map<string, string | null> {
	const removedSpineIndices = new Set<number>();
	for (const placement of branchPlacements) {
		for (const index of placement.replacedSpineIndices) {
			removedSpineIndices.add(index);
		}
	}
	const branchCorridorIdByCoordKey = new Map<string, string | null>();
	for (let index = 0; index < spine.length; index += 1) {
		if (removedSpineIndices.has(index)) {
			continue;
		}
		const cell = spine[index];
		if (cell) {
			branchCorridorIdByCoordKey.set(coordKey(cell.row, cell.col), null);
		}
	}
	for (const placement of branchPlacements) {
		for (const cell of placement.cells) {
			branchCorridorIdByCoordKey.set(
				coordKey(cell.row, cell.col),
				placement.branchId,
			);
		}
	}
	return branchCorridorIdByCoordKey;
}

function orderBranchCandidates(
	candidates: HexCoord[],
	joinTarget: HexCoord | null,
	rng: SeededRng,
): HexCoord[] {
	const weighted = candidates.map((candidate) => ({
		candidate,
		roll: rng.next(),
		distance: joinTarget
			? hexDistance(
					candidate.row,
					candidate.col,
					joinTarget.row,
					joinTarget.col,
				)
			: 0,
	}));
	weighted.sort((a, b) => {
		if (a.distance !== b.distance) {
			return a.distance - b.distance;
		}
		return a.roll - b.roll;
	});
	return weighted.map((entry) => entry.candidate);
}

function searchIncrementalPathAppend(params: {
	basePathCoordSet: Set<string>;
	occupiedKeys: Set<string>;
	existingBranchPlacements: BranchPlacement[];
	anchorCell: HexCoord;
	joinTarget: HexCoord | null;
	length: number;
	minRow: number;
	maxRow: number;
	minCol: number;
	maxCol: number;
	rng: SeededRng;
}): HexCoord[] | null {
	const {
		basePathCoordSet,
		occupiedKeys,
		existingBranchPlacements,
		anchorCell,
		joinTarget,
		length,
		minRow,
		maxRow,
		minCol,
		maxCol,
		rng,
	} = params;
	if (length <= 0) {
		return [];
	}
	const prospectivePlacementMeta: Pick<
		BranchPlacement,
		"anchorCell" | "joinCell"
	> = {
		anchorCell,
		joinCell: joinTarget,
	};
	const validationBranchPlacements = [
		...existingBranchPlacements,
		prospectivePlacementMeta,
	];
	const allowanceByCoord = buildJunctionAllowanceByCoord(
		validationBranchPlacements,
	);
	const corridor: HexCoord[] = [];
	const corridorKeys = new Set<string>();
	const activePathCoordSet = new Set(basePathCoordSet);
	const degreeByCoord = buildDegreeByCoord(activePathCoordSet);
	const canReachJoin = (
		cell: HexCoord,
		stepsRemainingAfterMove: number,
	): boolean => {
		if (!joinTarget) {
			return true;
		}
		return (
			hexDistance(cell.row, cell.col, joinTarget.row, joinTarget.col) <=
			stepsRemainingAfterMove + 1
		);
	};

	function search(from: HexCoord, depth: number): boolean {
		if (depth === length) {
			if (joinTarget && !areHexAdjacent(from, joinTarget)) {
				return false;
			}
			return isValidVisiblePathGraph(
				activePathCoordSet,
				validationBranchPlacements,
			);
		}
		const stepsRemainingAfterMove = length - depth - 1;
		const candidates = getNeighbors(from.row, from.col).filter(
			(candidate) => {
				const key = coordKey(candidate.row, candidate.col);
				if (
					candidate.row < minRow ||
					candidate.row > maxRow ||
					candidate.col < minCol ||
					candidate.col > maxCol
				) {
					return false;
				}
				if (
					occupiedKeys.has(key) ||
					activePathCoordSet.has(key) ||
					corridorKeys.has(key)
				) {
					return false;
				}
				if (!canReachJoin(candidate, stepsRemainingAfterMove)) {
					return false;
				}
				return canAddPathCellIncrementally({
					cell: candidate,
					activePathCoordSet,
					degreeByCoord,
					allowanceByCoord,
				});
			},
		);
		for (const candidate of orderBranchCandidates(
			candidates,
			joinTarget,
			rng,
		)) {
			const key = coordKey(candidate.row, candidate.col);
			corridor.push(candidate);
			corridorKeys.add(key);
			const adjacentKeys = addPathCellToState({
				cell: candidate,
				key,
				activePathCoordSet,
				degreeByCoord,
			});
			if (search(candidate, depth + 1)) {
				return true;
			}
			corridor.pop();
			corridorKeys.delete(key);
			removePathCellFromState({
				key,
				activePathCoordSet,
				degreeByCoord,
				adjacentKeys,
			});
		}
		return false;
	}

	return search(anchorCell, 0) ? corridor : null;
}

function placeBranchSegment(params: {
	branchId: string;
	length: number;
	spine: HexCoord[];
	branchPlacements: BranchPlacement[];
	occupiedKeys: Set<string>;
	anchorCandidates: number[];
	joinCandidates: Array<number | null>;
	minRow: number;
	maxRow: number;
	minCol: number;
	maxCol: number;
	rng: SeededRng;
}): BranchPlacement | null {
	const {
		branchId,
		length,
		spine,
		branchPlacements,
		occupiedKeys,
		anchorCandidates,
		joinCandidates,
		minRow,
		maxRow,
		minCol,
		maxCol,
		rng,
	} = params;
	for (const anchorSpineIndex of anchorCandidates) {
		const anchorCell = spine[anchorSpineIndex];
		if (!anchorCell) {
			continue;
		}
		for (const joinCandidate of joinCandidates) {
			if (
				typeof joinCandidate === "number" &&
				joinCandidate <= anchorSpineIndex
			) {
				continue;
			}
			const joinTarget =
				typeof joinCandidate === "number"
					? (spine[joinCandidate] ?? null)
					: null;
			const replacedSpineIndices = new Set<number>();
			if (typeof joinCandidate === "number") {
				for (
					let spineIndex = anchorSpineIndex + 1;
					spineIndex < joinCandidate;
					spineIndex += 1
				) {
					replacedSpineIndices.add(spineIndex);
				}
			}
			const basePathCoordSet = buildVisiblePathCoordSet(
				spine,
				branchPlacements,
				replacedSpineIndices,
			);
			const corridor = searchIncrementalPathAppend({
				basePathCoordSet,
				occupiedKeys,
				existingBranchPlacements: branchPlacements,
				anchorCell,
				joinTarget,
				length,
				minRow,
				maxRow,
				minCol,
				maxCol,
				rng,
			});
			if (!corridor) {
				continue;
			}
			return {
				branchId,
				cells: corridor,
				replacedSpineIndices,
				anchorCell,
				joinCell: joinTarget,
			};
		}
	}
	return null;
}

function buildSpinePlacements(
	graph: ModuleMapGraph,
	spine: HexCoord[],
): {
	spinePlacements: SpineNodePlacement[];
	spineCellByGraphIndex: Map<number, HexCoord>;
	spineIndexByGraphIndex: Map<number, number>;
} {
	const spinePlacements: SpineNodePlacement[] = [];
	const spineCellByGraphIndex = new Map<number, HexCoord>();
	const spineIndexByGraphIndex = new Map<number, number>();
	let spineCursor = 0;
	for (let graphIndex = 0; graphIndex < graph.nodes.length; graphIndex += 1) {
		const node = graph.nodes[graphIndex];
		if (!node || isBranchNode(node)) {
			continue;
		}
		const cell = spine[spineCursor] ??
			spine[spine.length - 1] ?? { row: 0, col: 0 };
		spinePlacements.push({
			graphIndex,
			spineIndex: spineCursor,
			node,
			cell,
		});
		spineCellByGraphIndex.set(graphIndex, cell);
		spineIndexByGraphIndex.set(graphIndex, spineCursor);
		spineCursor += 1;
	}
	return { spinePlacements, spineCellByGraphIndex, spineIndexByGraphIndex };
}

function assignNodesAlongSpine(
	graph: ModuleMapGraph,
	spine: HexCoord[],
	rows: number,
	cols: number,
	padding: { rows: number; cols: number },
	boundsSlack: { rows: number; cols: number },
	seed: string,
	limits?: { minRow: number; maxRow: number; minCol: number; maxCol: number },
): {
	pathCells: PathCell[];
	nodeById: Map<string, HexCoord>;
	branchPlacements: BranchPlacement[];
} {
	const pathCells: PathCell[] = [];
	const nodeById = new Map<string, HexCoord>();
	const occupiedKeys = new Set<string>();
	let minRow = padding.rows - boundsSlack.rows;
	let maxRow = Math.max(minRow, rows - 1 - padding.rows + boundsSlack.rows);
	let minCol = padding.cols - boundsSlack.cols;
	let maxCol = Math.max(minCol, cols - 1 - padding.cols + boundsSlack.cols);
	if (limits) {
		minRow = Math.max(minRow, limits.minRow);
		maxRow = Math.min(maxRow, limits.maxRow);
		minCol = Math.max(minCol, limits.minCol);
		maxCol = Math.min(maxCol, limits.maxCol);
	}
	const { spinePlacements, spineCellByGraphIndex, spineIndexByGraphIndex } =
		buildSpinePlacements(graph, spine);
	const branchPlacements: BranchPlacement[] = [];

	for (let graphIndex = 0; graphIndex < graph.nodes.length; ) {
		const node = graph.nodes[graphIndex];
		if (!node) {
			graphIndex += 1;
			continue;
		}
		const branchId = nodeBranchId(node);
		if (branchId) {
			const segmentNodes: MapNode[] = [];
			let segmentEndIndex = graphIndex;
			while (segmentEndIndex < graph.nodes.length) {
				const segmentNode = graph.nodes[segmentEndIndex];
				if (!segmentNode || nodeBranchId(segmentNode) !== branchId) {
					break;
				}
				segmentNodes.push(segmentNode);
				segmentEndIndex += 1;
			}
			let originalAnchorGraphIndex = graphIndex - 1;
			while (originalAnchorGraphIndex >= 0) {
				const candidate = graph.nodes[originalAnchorGraphIndex];
				if (candidate?.kind === "content" && !isBranchNode(candidate)) {
					break;
				}
				originalAnchorGraphIndex -= 1;
			}
			if (originalAnchorGraphIndex < 0) {
				originalAnchorGraphIndex = graphIndex - 1;
				while (originalAnchorGraphIndex >= 0) {
					const candidate = graph.nodes[originalAnchorGraphIndex];
					if (candidate && !isBranchNode(candidate)) {
						break;
					}
					originalAnchorGraphIndex -= 1;
				}
			}
			const originalAnchorSpineIndex = spineIndexByGraphIndex.get(
				originalAnchorGraphIndex,
			);
			if (originalAnchorSpineIndex === undefined) {
				throw new BranchPlacementError(
					`[hexagon-map] Missing branch anchor for corridor ${branchId}`,
				);
			}
			let joinGraphIndex = segmentEndIndex;
			while (joinGraphIndex < graph.nodes.length) {
				const candidate = graph.nodes[joinGraphIndex];
				if (candidate?.kind === "content" && !isBranchNode(candidate)) {
					break;
				}
				joinGraphIndex += 1;
			}
			if (
				nodeBranchRejoins(node) &&
				joinGraphIndex >= graph.nodes.length
			) {
				joinGraphIndex = segmentEndIndex;
				while (joinGraphIndex < graph.nodes.length) {
					const candidate = graph.nodes[joinGraphIndex];
					if (candidate && !isBranchNode(candidate)) {
						break;
					}
					joinGraphIndex += 1;
				}
			}
			const joinSpineIndex = nodeBranchRejoins(node)
				? spineIndexByGraphIndex.get(joinGraphIndex)
				: undefined;
			if (nodeBranchRejoins(node) && joinSpineIndex === undefined) {
				throw new BranchPlacementError(
					`[hexagon-map] Missing branch rejoin target for corridor ${branchId}`,
				);
			}
			const preferredAnchorCandidates =
				joinSpineIndex !== undefined
					? walkSpineIndices({
							startIndex: originalAnchorSpineIndex,
							minIndex: originalAnchorSpineIndex,
							maxIndex: Math.max(
								originalAnchorSpineIndex,
								joinSpineIndex - 1,
							),
							direction: "forward",
						})
					: [originalAnchorSpineIndex];
			const localFallbackRadius = Math.max(
				2,
				Math.min(
					HEXAGON_MAP_BRANCH_LOCAL_FALLBACK_RADIUS,
					segmentNodes.length + 2,
				),
			);
			const anchorFallbackMaxIndex = Math.max(
				originalAnchorSpineIndex,
				(joinSpineIndex ?? originalAnchorSpineIndex) - 1,
			);
			const fallbackAnchorCandidates = walkSpineIndices({
				startIndex: originalAnchorSpineIndex,
				minIndex: Math.max(
					0,
					originalAnchorSpineIndex - localFallbackRadius,
				),
				maxIndex:
					joinSpineIndex !== undefined
						? anchorFallbackMaxIndex
						: Math.min(
								spinePlacements.length - 1,
								originalAnchorSpineIndex + localFallbackRadius,
							),
				direction: "both",
				excluded: new Set(preferredAnchorCandidates),
			});
			const preferredJoinCandidates: Array<number | null> =
				joinSpineIndex !== undefined ? [joinSpineIndex] : [null];
			const fallbackJoinCandidates: Array<number | null> =
				joinSpineIndex !== undefined
					? Array.from(
							new Set([
								...walkSpineIndices({
									startIndex: joinSpineIndex,
									minIndex: Math.max(
										originalAnchorSpineIndex + 1,
										joinSpineIndex - localFallbackRadius,
									),
									maxIndex: Math.min(
										spinePlacements.length - 1,
										joinSpineIndex + localFallbackRadius,
									),
									direction: "both",
								}),
								...walkSpineIndices({
									startIndex: joinSpineIndex,
									minIndex: originalAnchorSpineIndex + 1,
									maxIndex: spinePlacements.length - 1,
									direction: "forward",
								}),
							]),
						)
					: [null];
			let placement: BranchPlacement | null = null;
			for (
				let subattempt = 1;
				subattempt <= HEXAGON_MAP_BRANCH_SUBATTEMPTS && !placement;
				subattempt += 1
			) {
				const branchRng = createSeededRng(
					`${seed}:branches:${branchId}:subattempt:${subattempt}`,
				);
				placement =
					placeBranchSegment({
						branchId,
						length: segmentNodes.length,
						spine,
						branchPlacements,
						occupiedKeys,
						anchorCandidates: preferredAnchorCandidates,
						joinCandidates: preferredJoinCandidates,
						minRow,
						maxRow,
						minCol,
						maxCol,
						rng: branchRng,
					}) ??
					placeBranchSegment({
						branchId,
						length: segmentNodes.length,
						spine,
						branchPlacements,
						occupiedKeys,
						anchorCandidates: fallbackAnchorCandidates,
						joinCandidates: fallbackJoinCandidates,
						minRow,
						maxRow,
						minCol,
						maxCol,
						rng: branchRng,
					});
			}
			if (!placement) {
				console.warn("[hexagon-map] branch placement failed", {
					seed,
					branchId,
					segmentLength: segmentNodes.length,
					anchorGraphIndex: originalAnchorGraphIndex,
					anchorSpineIndex: originalAnchorSpineIndex,
					joinGraphIndex:
						nodeBranchRejoins(node) &&
						joinGraphIndex < graph.nodes.length
							? joinGraphIndex
							: null,
					joinSpineIndex: joinSpineIndex ?? null,
					preferredAnchorCandidates,
					fallbackAnchorCandidates,
					preferredJoinCandidates,
					fallbackJoinCandidates,
				});
				throw new BranchPlacementError(
					`[hexagon-map] Unable to place branch corridor ${branchId}`,
				);
			}
			branchPlacements.push(placement);
			for (let offset = 0; offset < segmentNodes.length; offset += 1) {
				const segmentNode = segmentNodes[offset];
				const cell = placement.cells[offset];
				if (!segmentNode || !cell) {
					continue;
				}
				occupiedKeys.add(coordKey(cell.row, cell.col));
				nodeById.set(segmentNode.id, cell);
				pathCells.push({
					row: cell.row,
					col: cell.col,
					nodeId: segmentNode.id,
					pathIndex: graphIndex + offset,
					branchCorridorId: branchId,
					branchRejoins: nodeBranchRejoins(segmentNode),
				});
			}
			graphIndex = segmentEndIndex;
			continue;
		}
		const spineCell = spineCellByGraphIndex.get(graphIndex) ??
			spine[spine.length - 1] ?? {
				row: 0,
				col: 0,
			};
		occupiedKeys.add(coordKey(spineCell.row, spineCell.col));
		nodeById.set(node.id, spineCell);
		pathCells.push({
			row: spineCell.row,
			col: spineCell.col,
			nodeId: node.id,
			pathIndex: graphIndex,
		});
		graphIndex += 1;
	}

	return { pathCells, nodeById, branchPlacements };
}

function buildPathCoordSet(
	spine: HexCoord[],
	branchPlacements: BranchPlacement[],
): Set<string> {
	return buildVisiblePathCoordSet(spine, branchPlacements);
}

function normalizeLayoutBounds(
	spine: HexCoord[],
	pathCells: PathCell[],
	branchPlacements: BranchPlacement[],
	nodeById: Map<string, HexCoord>,
	startCell: HexCoord,
	endCell: HexCoord,
	padding: { rows: number; cols: number },
): {
	spine: HexCoord[];
	pathCells: PathCell[];
	nodeById: Map<string, HexCoord>;
	startCell: HexCoord;
	endCell: HexCoord;
	rows: number;
	cols: number;
} {
	const visibleOccupiedCoords = [
		...buildVisiblePathCoordSet(spine, branchPlacements),
	].map(parseCoordKeyToCoord);
	const occupiedCoords =
		visibleOccupiedCoords.length > 0
			? visibleOccupiedCoords
			: [
					...spine,
					...pathCells.map((cell) => ({
						row: cell.row,
						col: cell.col,
					})),
				];
	let minSpineRow = spine[0]?.row ?? 0;
	let maxSpineRow = minSpineRow;
	let minAnyRow = occupiedCoords[0]?.row ?? 0;
	let maxAnyRow = minAnyRow;
	let minSpineCol = spine[0]?.col ?? 0;
	let maxSpineCol = minSpineCol;
	let minAnyCol = occupiedCoords[0]?.col ?? 0;
	let maxAnyCol = minAnyCol;

	for (const coord of spine) {
		minSpineRow = Math.min(minSpineRow, coord.row);
		maxSpineRow = Math.max(maxSpineRow, coord.row);
		minSpineCol = Math.min(minSpineCol, coord.col);
		maxSpineCol = Math.max(maxSpineCol, coord.col);
	}
	for (const coord of occupiedCoords) {
		minAnyRow = Math.min(minAnyRow, coord.row);
		maxAnyRow = Math.max(maxAnyRow, coord.row);
		minAnyCol = Math.min(minAnyCol, coord.col);
		maxAnyCol = Math.max(maxAnyCol, coord.col);
	}

	const desiredRowShift = padding.rows - minAnyRow;
	const rowShift =
		Math.abs(desiredRowShift % 2) === 1
			? desiredRowShift + 1
			: desiredRowShift;
	const rawCenterCol = endCell.col;
	const leftExtent = Math.max(
		rawCenterCol - minSpineCol,
		rawCenterCol - minAnyCol,
		0,
	);
	const rightExtent = Math.max(
		maxSpineCol - rawCenterCol,
		maxAnyCol - rawCenterCol,
		0,
	);
	const horizontalExtent = Math.max(leftExtent, rightExtent);
	const centerCol = padding.cols + horizontalExtent;
	const colShift = centerCol - rawCenterCol;
	const shiftCoord = (coord: HexCoord): HexCoord => ({
		row: coord.row + rowShift,
		col: coord.col + colShift,
	});

	const shiftedSpine = spine.map(shiftCoord);
	const shiftedPathCells = pathCells.map((cell) => ({
		...cell,
		row: cell.row + rowShift,
		col: cell.col + colShift,
	}));
	const shiftedNodeById = new Map<string, HexCoord>();
	for (const [id, coord] of nodeById) {
		shiftedNodeById.set(id, shiftCoord(coord));
	}

	const shiftedMaxRow = maxAnyRow + rowShift;

	return {
		spine: shiftedSpine,
		pathCells: shiftedPathCells,
		nodeById: shiftedNodeById,
		startCell: shiftCoord(startCell),
		endCell: shiftCoord(endCell),
		rows: shiftedMaxRow + padding.rows + 1,
		cols: horizontalExtent * 2 + padding.cols * 2 + 1,
	};
}

function generateStraightSpine(
	targetLength: number,
	padding: { rows: number; cols: number },
): HexCoord[] {
	const path: HexCoord[] = [];
	for (let i = 0; i < targetLength; i++) {
		path.push({
			row: padding.rows + (targetLength - 1 - i),
			col: Math.floor((padding.cols * 2 + 7) / 2),
		});
	}
	return path;
}

function applyLayoutCoordinateShift(
	layout: MapLayout,
	shift: { row?: number; col?: number },
	nextDimensions: { rows: number; cols: number },
): MapLayout {
	const rowOffset = shift.row ?? 0;
	const colOffset = shift.col ?? 0;
	if (
		rowOffset === 0 &&
		colOffset === 0 &&
		nextDimensions.rows === layout.rows &&
		nextDimensions.cols === layout.cols
	) {
		return layout;
	}

	const shiftRow = (row: number) => row + rowOffset;
	const shiftCol = (col: number) => col + colOffset;
	const pathCoordSet = new Set<string>();
	for (const key of layout.pathCoordSet) {
		const [row, col] = key.split(",").map(Number);
		if (row !== undefined && col !== undefined) {
			pathCoordSet.add(coordKey(shiftRow(row), shiftCol(col)));
		}
	}

	const nodeById = new Map<string, HexCoord>();
	for (const [id, coord] of layout.nodeById) {
		nodeById.set(id, {
			row: shiftRow(coord.row),
			col: shiftCol(coord.col),
		});
	}

	const branchCorridorIdByCoordKey = new Map<string, string | null>();
	for (const [key, branchCorridorId] of layout.branchCorridorIdByCoordKey) {
		const [row, col] = key.split(",").map(Number);
		if (row !== undefined && col !== undefined) {
			branchCorridorIdByCoordKey.set(
				coordKey(shiftRow(row), shiftCol(col)),
				branchCorridorId,
			);
		}
	}

	return {
		...layout,
		rows: nextDimensions.rows,
		cols: nextDimensions.cols,
		spinePath: layout.spinePath.map((coord) => ({
			row: shiftRow(coord.row),
			col: shiftCol(coord.col),
		})),
		pathCoordSet,
		pathCells: layout.pathCells.map((cell) => ({
			...cell,
			row: shiftRow(cell.row),
			col: shiftCol(cell.col),
		})),
		nodeById,
		branchCorridorIdByCoordKey,
		startCell: {
			row: shiftRow(layout.startCell.row),
			col: shiftCol(layout.startCell.col),
		},
		endCell: {
			row: shiftRow(layout.endCell.row),
			col: shiftCol(layout.endCell.col),
		},
	};
}

/** Center path within a wider display grid (container width fill). */
export function shiftLayoutColumns(
	layout: MapLayout,
	displayCols: number,
): MapLayout {
	if (displayCols <= layout.cols) {
		return layout;
	}

	let minOccupiedCol = Number.POSITIVE_INFINITY;
	let maxOccupiedCol = Number.NEGATIVE_INFINITY;
	for (const key of layout.pathCoordSet) {
		const [, col] = key.split(",").map(Number);
		if (col === undefined || Number.isNaN(col)) {
			continue;
		}
		minOccupiedCol = Math.min(minOccupiedCol, col);
		maxOccupiedCol = Math.max(maxOccupiedCol, col);
	}
	if (!Number.isFinite(minOccupiedCol) || !Number.isFinite(maxOccupiedCol)) {
		minOccupiedCol = 0;
		maxOccupiedCol = Math.max(0, layout.cols - 1);
	}

	const occupiedWidth = Math.max(1, maxOccupiedCol - minOccupiedCol + 1);
	const desiredOccupiedMinCol = Math.max(
		0,
		Math.floor((displayCols - occupiedWidth) / 2),
	);
	const minOffset = -minOccupiedCol;
	const maxOffset = displayCols - 1 - maxOccupiedCol;
	const preferredOffset = desiredOccupiedMinCol - minOccupiedCol;
	const offset = Math.max(minOffset, Math.min(preferredOffset, maxOffset));

	return applyLayoutCoordinateShift(
		layout,
		{ col: offset },
		{ rows: layout.rows, cols: displayCols },
	);
}

/** Center path within a taller display grid (container height fill). */
export function shiftLayoutRows(
	layout: MapLayout,
	displayRows: number,
): MapLayout {
	if (displayRows <= layout.rows) {
		return layout;
	}

	let minOccupiedRow = Number.POSITIVE_INFINITY;
	let maxOccupiedRow = Number.NEGATIVE_INFINITY;
	for (const key of layout.pathCoordSet) {
		const [row] = key.split(",").map(Number);
		if (row === undefined || Number.isNaN(row)) {
			continue;
		}
		minOccupiedRow = Math.min(minOccupiedRow, row);
		maxOccupiedRow = Math.max(maxOccupiedRow, row);
	}
	if (!Number.isFinite(minOccupiedRow) || !Number.isFinite(maxOccupiedRow)) {
		minOccupiedRow = 0;
		maxOccupiedRow = Math.max(0, layout.rows - 1);
	}

	const occupiedHeight = Math.max(1, maxOccupiedRow - minOccupiedRow + 1);
	const desiredOccupiedMinRow = Math.max(
		0,
		Math.floor((displayRows - occupiedHeight) / 2),
	);
	const minOffset = -minOccupiedRow;
	const maxOffset = displayRows - 1 - maxOccupiedRow;
	const preferredOffset = desiredOccupiedMinRow - minOccupiedRow;
	let offset = 0;
	let bestDistance = Number.POSITIVE_INFINITY;
	for (let candidate = minOffset; candidate <= maxOffset; candidate += 1) {
		if (Math.abs(candidate % 2) === 1) {
			continue;
		}
		const distance = Math.abs(candidate - preferredOffset);
		if (distance < bestDistance) {
			offset = candidate;
			bestDistance = distance;
		}
	}

	return applyLayoutCoordinateShift(
		layout,
		{ row: offset },
		{ rows: displayRows, cols: layout.cols },
	);
}

export function computeMapLayout(
	graph: ModuleMapGraph,
	options: ComputeMapLayoutOptions,
): MapLayout {
	const padding = resolveMapPathPadding(options.pathPadding);
	const direction = options.direction ?? "bottom-top";
	const targetLength = countSpineNodes(graph);
	const branchNodeCount = Math.max(0, graph.nodes.length - targetLength);
	const branchBoundsSlack = {
		rows: Math.max(0, Math.ceil(branchNodeCount / 3)),
		cols: Math.max(0, Math.ceil(branchNodeCount / 2)),
	};
	const isExactSeed = options.exactSeed ?? false;
	const maxAttempts = isExactSeed ? 1 : (options.maxAttempts ?? 50);
	const maxDurationMs = options.maxDurationMs;
	const generationStartedAt = Date.now();
	const logAttempts = options.logAttempts ?? true;
	if (logAttempts) {
		logHexagonMapObservation("layout generation start", {
			seed: options.seed,
			exactSeed: isExactSeed,
			targetLength,
			maxAttempts,
			maxDurationMs: maxDurationMs ?? null,
			direction,
			pathPadding: padding,
			branchCorridorCount: graph.branchCorridorIds.length,
		});
	}

	if (targetLength === 0) {
		return {
			rows: padding.rows * 2 + 1,
			cols: padding.cols * 2 + 7,
			spinePath: [],
			pathCoordSet: new Set(),
			pathCells: [],
			nodeById: new Map(),
			branchCorridors: graph.branchCorridorIds,
			branchCorridorIdByCoordKey: new Map(),
			startCell: { row: 0, col: 0 },
			endCell: { row: 0, col: 0 },
			pathPadding: padding,
			direction,
			generationAttempt: 0,
		};
	}

	if (options.forceStraightSpine) {
		const generated = normalizePathToGrid(
			generateStraightSpine(targetLength, padding),
			padding,
			direction,
		);
		const minorRange =
			options.minorRange != null
				? Math.max(1, Math.floor(options.minorRange))
				: 0;
		const limits =
			minorRange > 0
				? {
						minRow: padding.rows,
						maxRow: Math.max(
							padding.rows,
							generated.rows - 1 - padding.rows,
						),
						minCol: padding.cols - minorRange,
						maxCol: padding.cols + minorRange,
					}
				: undefined;
		const { pathCells, nodeById, branchPlacements } = assignNodesAlongSpine(
			graph,
			generated.path,
			generated.rows,
			generated.cols,
			padding,
			branchBoundsSlack,
			isExactSeed
				? options.seed
				: buildMapLayoutAttemptSeed(options.seed, 1),
			limits,
		);
		const normalizedLayout = normalizeLayoutBounds(
			generated.path,
			pathCells,
			branchPlacements,
			nodeById,
			generated.startCell,
			generated.endCell,
			padding,
		);
		const normalizedBranchPlacements = branchPlacements.map(
			(placement) => ({
				...placement,
				cells: placement.cells.map((cell) => ({
					row:
						cell.row +
						(normalizedLayout.startCell.row -
							generated.startCell.row),
					col:
						cell.col +
						(normalizedLayout.startCell.col -
							generated.startCell.col),
				})),
				anchorCell: {
					row:
						placement.anchorCell.row +
						(normalizedLayout.startCell.row -
							generated.startCell.row),
					col:
						placement.anchorCell.col +
						(normalizedLayout.startCell.col -
							generated.startCell.col),
				},
				joinCell: placement.joinCell
					? {
							row:
								placement.joinCell.row +
								(normalizedLayout.startCell.row -
									generated.startCell.row),
							col:
								placement.joinCell.col +
								(normalizedLayout.startCell.col -
									generated.startCell.col),
						}
					: null,
			}),
		);
		const pathCoordSet = buildPathCoordSet(
			normalizedLayout.spine,
			normalizedBranchPlacements,
		);
		const branchCorridorIdByCoordKey = buildBranchCorridorIdByCoordKey(
			normalizedLayout.spine,
			normalizedBranchPlacements,
		);
		const resolvedStartCell = normalizedLayout.pathCells[0]
			? {
					row: normalizedLayout.pathCells[0].row,
					col: normalizedLayout.pathCells[0].col,
				}
			: normalizedLayout.startCell;
		const resolvedEndCell = normalizedLayout.pathCells[
			normalizedLayout.pathCells.length - 1
		]
			? {
					row:
						normalizedLayout.pathCells[
							normalizedLayout.pathCells.length - 1
						]?.row ?? normalizedLayout.endCell.row,
					col:
						normalizedLayout.pathCells[
							normalizedLayout.pathCells.length - 1
						]?.col ?? normalizedLayout.endCell.col,
				}
			: normalizedLayout.endCell;
		return {
			rows: normalizedLayout.rows,
			cols: normalizedLayout.cols,
			spinePath: normalizedLayout.spine,
			pathCoordSet,
			pathCells: normalizedLayout.pathCells,
			nodeById: normalizedLayout.nodeById,
			branchCorridors: graph.branchCorridorIds,
			branchCorridorIdByCoordKey,
			startCell: resolvedStartCell,
			endCell: resolvedEndCell,
			pathPadding: padding,
			direction,
			generationAttempt: 1,
		};
	}

	const display = buildDisplayGridGeneration(
		targetLength,
		branchNodeCount,
		options,
	);
	let lastPlacementError: Error | undefined;
	let attemptsTried = 0;

	for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
		if (
			maxDurationMs != null &&
			attempt > 1 &&
			Date.now() - generationStartedAt >= maxDurationMs
		) {
			lastPlacementError = new Error(
				`[hexagon-map] Layout generation exceeded ${maxDurationMs}ms budget after ${attemptsTried} attempts`,
			);
			break;
		}

		attemptsTried = attempt;
		const attemptStartedAt = Date.now();
		const attemptSeed = isExactSeed
			? options.seed
			: buildMapLayoutAttemptSeed(options.seed, attempt);
		const rng = createSeededRng(attemptSeed);
		const endRowMin = display.config.endRowMin ?? display.config.endRow;
		const endRowMax = display.config.endRowMax ?? display.config.endRow;
		const resolvedEndRow =
			endRowMin === endRowMax
				? endRowMin
				: endRowMin + rng.int(endRowMax - endRowMin + 1);
		const attemptConfig: PathGeneratorConfig = {
			...display.config,
			endRow: resolvedEndRow,
			endRowMin: resolvedEndRow,
			endRowMax: resolvedEndRow,
		};
		const rawSpine = generateFixedLengthPath(
			attemptConfig,
			targetLength,
			rng,
		);
		if (!rawSpine) {
			if (logAttempts) {
				logHexagonMapObservation("layout generation attempt failed", {
					seed: options.seed,
					attemptSeed,
					attempt,
					maxAttempts,
					targetLength,
					phase: "spine-generation",
					attemptElapsedMs: Date.now() - attemptStartedAt,
					totalElapsedMs: Date.now() - generationStartedAt,
				});
			}
			continue;
		}
		try {
			const generated = normalizePathToGrid(rawSpine, padding, direction);
			const limits = {
				minRow: display.config.startRow + display.config.yMinRange,
				maxRow: display.config.startRow + display.config.yMaxRange,
				minCol: display.config.minCol ?? padding.cols,
				maxCol:
					display.config.maxCol ??
					Math.max(padding.cols, generated.cols - 1 - padding.cols),
			};
			const { pathCells, nodeById, branchPlacements } =
				assignNodesAlongSpine(
					graph,
					generated.path,
					generated.rows,
					generated.cols,
					padding,
					branchBoundsSlack,
					attemptSeed,
					limits,
				);
			const normalizedLayout = normalizeLayoutBounds(
				generated.path,
				pathCells,
				branchPlacements,
				nodeById,
				generated.startCell,
				generated.endCell,
				padding,
			);
			const rowShift =
				normalizedLayout.startCell.row - generated.startCell.row;
			const colShift =
				normalizedLayout.startCell.col - generated.startCell.col;
			const normalizedBranchPlacements = branchPlacements.map(
				(placement) => ({
					...placement,
					cells: placement.cells.map((cell) => ({
						row: cell.row + rowShift,
						col: cell.col + colShift,
					})),
					anchorCell: {
						row: placement.anchorCell.row + rowShift,
						col: placement.anchorCell.col + colShift,
					},
					joinCell: placement.joinCell
						? {
								row: placement.joinCell.row + rowShift,
								col: placement.joinCell.col + colShift,
							}
						: null,
				}),
			);
			if (!isValidPath(normalizedLayout.spine)) {
				throw new BranchPlacementError(
					`[hexagon-map] Spine fails isValidPath after bounds normalization (seed=${options.seed}, length=${normalizedLayout.spine.length})`,
				);
			}
			const pathCoordSet = buildPathCoordSet(
				normalizedLayout.spine,
				normalizedBranchPlacements,
			);
			const branchCorridorIdByCoordKey = buildBranchCorridorIdByCoordKey(
				normalizedLayout.spine,
				normalizedBranchPlacements,
			);
			if (
				!isValidVisiblePathGraph(
					pathCoordSet,
					normalizedBranchPlacements,
				)
			) {
				throw new BranchPlacementError(
					`[hexagon-map] Visible path graph invalid after branch placement (seed=${options.seed}, attempt=${attempt})`,
				);
			}
			const resolvedStartCell = normalizedLayout.pathCells[0]
				? {
						row: normalizedLayout.pathCells[0].row,
						col: normalizedLayout.pathCells[0].col,
					}
				: normalizedLayout.startCell;
			const resolvedEndCell = normalizedLayout.pathCells[
				normalizedLayout.pathCells.length - 1
			]
				? {
						row:
							normalizedLayout.pathCells[
								normalizedLayout.pathCells.length - 1
							]?.row ?? normalizedLayout.endCell.row,
						col:
							normalizedLayout.pathCells[
								normalizedLayout.pathCells.length - 1
							]?.col ?? normalizedLayout.endCell.col,
					}
				: normalizedLayout.endCell;
			const minPrimaryAxisSeparation =
				resolveMinimumPrimaryAxisSeparation(
					normalizedLayout.spine.length,
				);
			if (
				resolvePrimaryAxisDistance(
					resolvedStartCell,
					resolvedEndCell,
					direction,
				) < minPrimaryAxisSeparation
			) {
				throw new BranchPlacementError(
					`[hexagon-map] Primary-axis separation too small after normalization (seed=${options.seed}, attempt=${attempt}, min=${minPrimaryAxisSeparation})`,
				);
			}
			if (logAttempts) {
				logHexagonMapObservation(
					"layout generation attempt succeeded",
					{
						seed: options.seed,
						attemptSeed,
						attempt,
						maxAttempts,
						targetLength,
						rows: normalizedLayout.rows,
						cols: normalizedLayout.cols,
						pathCellCount: normalizedLayout.pathCells.length,
						attemptElapsedMs: Date.now() - attemptStartedAt,
						totalElapsedMs: Date.now() - generationStartedAt,
					},
				);
			}
			return {
				rows: normalizedLayout.rows,
				cols: normalizedLayout.cols,
				spinePath: normalizedLayout.spine,
				pathCoordSet,
				pathCells: normalizedLayout.pathCells,
				nodeById: normalizedLayout.nodeById,
				branchCorridors: graph.branchCorridorIds,
				branchCorridorIdByCoordKey,
				startCell: resolvedStartCell,
				endCell: resolvedEndCell,
				pathPadding: padding,
				direction,
				generationAttempt: attempt,
			};
		} catch (error) {
			lastPlacementError =
				error instanceof Error ? error : new Error(String(error));
			if (logAttempts) {
				logHexagonMapObservation("layout generation attempt failed", {
					seed: options.seed,
					attemptSeed,
					attempt,
					maxAttempts,
					targetLength,
					phase: "layout-validation",
					error: lastPlacementError.message,
					attemptElapsedMs: Date.now() - attemptStartedAt,
					totalElapsedMs: Date.now() - generationStartedAt,
				});
			}
		}
	}

	if (logAttempts) {
		logHexagonMapObservation("layout generation failed", {
			seed: options.seed,
			exactSeed: isExactSeed,
			targetLength,
			attemptsTried,
			totalElapsedMs: Date.now() - generationStartedAt,
			error: lastPlacementError?.message ?? null,
		});
	}

	throw new Error(
		`[hexagon-map] Failed to generate valid path and branch placement after ${attemptsTried} attempts (seed=${options.seed}, length=${targetLength})${
			lastPlacementError ? `: ${lastPlacementError.message}` : ""
		}`,
	);
}

export function resolvePlayerPathCell(
	playerStopId: string | null | undefined,
	graph: ModuleMapGraph,
	layout: Pick<MapLayout, "pathCells">,
): PathCell | null {
	if (!playerStopId) {
		return layout.pathCells[0] ?? null;
	}
	const nodeId = graph.stopIdToNodeId.get(playerStopId);
	if (!nodeId) {
		return layout.pathCells[0] ?? null;
	}
	for (const cell of layout.pathCells) {
		if (cell.nodeId === nodeId) {
			return cell;
		}
	}
	return layout.pathCells[0] ?? null;
}

export function resolvePlayerPathIndex(
	playerStopId: string | null | undefined,
	graph: ModuleMapGraph,
	layout: MapLayout,
): number {
	const cell = resolvePlayerPathCell(playerStopId, graph, layout);
	return cell?.pathIndex ?? 0;
}

export function buildPathFocusOrder(
	graph: ModuleMapGraph,
	layout: MapLayout,
): Array<{
	pathIndex: number;
	row: number;
	col: number;
	stopId?: string;
	kind: MapNode["kind"];
}> {
	const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
	return layout.pathCells
		.filter((cell) => {
			if (!cell.nodeId) {
				return false;
			}
			const node = nodeById.get(cell.nodeId);
			return node?.kind === "content";
		})
		.map((cell) => {
			const node = cell.nodeId ? nodeById.get(cell.nodeId) : undefined;
			return {
				pathIndex: cell.pathIndex,
				row: cell.row,
				col: cell.col,
				stopId: node?.kind === "content" ? node.stopId : undefined,
				kind: node?.kind ?? "spacer",
			};
		});
}

export function validateMapLayoutInvariants(
	graph: ModuleMapGraph,
	layout: MapLayout,
): string[] {
	const errors: string[] = [];
	const padding = layout.pathPadding ?? resolveMapPathPadding();

	const expectedSpineNodeCount = countSpineNodes(graph);
	if (layout.spinePath.length !== expectedSpineNodeCount) {
		errors.push(
			`spine length ${layout.spinePath.length} !== on-spine node count ${expectedSpineNodeCount}`,
		);
	}

	const start = layout.pathCells[0];
	if (start) {
		switch (layout.direction ?? "bottom-top") {
			case "top-bottom": {
				const expectedStartRow = padding.rows;
				if (Math.abs(start.row - expectedStartRow) > 1) {
					errors.push(
						`start row ${start.row} expected near ${expectedStartRow}`,
					);
				}
				break;
			}
			case "left-right": {
				const expectedStartCol = padding.cols;
				if (Math.abs(start.col - expectedStartCol) > 1) {
					errors.push(
						`start col ${start.col} expected near ${expectedStartCol}`,
					);
				}
				break;
			}
			case "right-left": {
				const expectedStartCol = layout.cols - 1 - padding.cols;
				if (Math.abs(start.col - expectedStartCol) > 1) {
					errors.push(
						`start col ${start.col} expected near ${expectedStartCol}`,
					);
				}
				break;
			}
			default: {
				const expectedStartRow = layout.rows - 1 - padding.rows;
				if (Math.abs(start.row - expectedStartRow) > 1) {
					errors.push(
						`start row ${start.row} expected near ${expectedStartRow}`,
					);
				}
			}
		}
	}
	if (layout.spinePath.length > 1) {
		const minPrimaryAxisSeparation = resolveMinimumPrimaryAxisSeparation(
			layout.spinePath.length,
		);
		const direction = layout.direction ?? "bottom-top";
		const primaryAxisDistance = resolvePrimaryAxisDistance(
			layout.startCell,
			layout.endCell,
			direction,
		);
		if (primaryAxisDistance < minPrimaryAxisSeparation) {
			errors.push(
				`primary-axis separation ${primaryAxisDistance} below minimum ${minPrimaryAxisSeparation}`,
			);
		}
	}

	for (const p of layout.spinePath) {
		if (!layout.pathCoordSet.has(coordKey(p.row, p.col))) {
			errors.push(
				`spine cell missing from pathCoordSet: ${p.row},${p.col}`,
			);
		}
	}

	if (!isValidPath(layout.spinePath)) {
		errors.push(
			"spinePath fails hexagon-background isValidPath (degree !== 2 on spine)",
		);
	}

	for (let i = 0; i < layout.spinePath.length - 1; i++) {
		const a = layout.spinePath[i];
		const b = layout.spinePath[i + 1];
		if (a && b && !areHexAdjacent(a, b)) {
			errors.push(
				`spine gap at index ${i}: ${coordKey(a.row, a.col)} -> ${coordKey(b.row, b.col)}`,
			);
		}
	}

	const coords = new Set<string>();
	for (const cell of layout.pathCells) {
		const key = coordKey(cell.row, cell.col);
		if (coords.has(key)) {
			errors.push(`duplicate node placement at ${key}`);
		}
		coords.add(key);
	}

	if (!isValidVisiblePathGraph(layout.pathCoordSet)) {
		errors.push("pathCoordSet fails global degree/connectivity validation");
	}

	return errors;
}
